import { useMemo } from 'react';

import { getRolesLOV, type RoleSummary } from 'api/roles/operations/getRolesLOV';

import { EditReference } from './EditReference';

interface Props {
  // The stored owner_role id (a config value), or null for "no owner". A string because the config
  // has always stored the id as a string (the old <Form.Select> option value); numeric ids coerce.
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

const getKey = (r: RoleSummary) => r.role_id;
const getLabel = (r: RoleSummary): string => r.name;

/**
 * Searchable dropdown for roles keyed on `role_id` — the id↔display-name expansion is internal: the
 * caller works in the stored id, and the eager LOV resolves it to a name via `findByKey` (the role
 * universe is small, so no server `resolve` is needed). Selection returns the id as a string, matching
 * how the config has always stored it.
 */
export const EditRoleReference = ({ value, onChange, label = 'Owner Role', mandatory, disabled }: Props) => {
  // Inflate the stored id to the partial summary EditReference selects on; the empty name defers label
  // resolution to the eager LOV's findByKey. Memoised on the id so the reference model isn't rebuilt each render.
  const selected = useMemo<RoleSummary | null>(() => (value ? { role_id: Number(value), name: '' } : null), [value]);

  return (
    <EditReference<RoleSummary>
      label={label}
      value={selected}
      onChange={(r) => onChange(r !== null ? String(r.role_id) : null)}
      source={getRolesLOV}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
    />
  );
};
